import { TITLE_COLUMN_WIDTH_KEY } from '@couplespace/core/databases';

import type { BlockRecord } from '@/lib/blocks/types';
import type { BlockPropertyRecord, DatabaseColumnDefinition } from '@/lib/databases/types';
import { formatCellDisplay, type PropertyValuePayload } from '@/lib/databases/values';

import { clampColumnWidth } from './column-layout';

const FONT_HEADER =
  '600 11px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_CELL =
  '400 14px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

const HEADER_EXTRA_PX = 44;
const CELL_EXTRA_PX = 36;
const TITLE_CELL_EXTRA_PX = 40;
const CHECKBOX_COLUMN_WIDTH = 56;

let measureCtx: CanvasRenderingContext2D | null = null;

function measureText(text: string, font: string): number {
  const sample = text.trim() || ' ';
  if (typeof document === 'undefined') {
    return sample.length * 8;
  }
  if (!measureCtx) {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) {
    return sample.length * 8;
  }
  measureCtx.font = font;
  return measureCtx.measureText(sample).width;
}

function propertyPayload(
  property: BlockPropertyRecord | undefined,
): PropertyValuePayload | undefined {
  if (!property?.value || typeof property.value !== 'object' || !('t' in property.value)) {
    return undefined;
  }
  return property.value as PropertyValuePayload;
}

export type ColumnAutoSizeInput = {
  columns: DatabaseColumnDefinition[];
  visibleCols: DatabaseColumnDefinition[];
  rows: BlockRecord[];
  propertiesByRowId: Record<string, BlockPropertyRecord[]>;
};

export function autoSizeTitleColumnWidth(input: ColumnAutoSizeInput): number {
  let max = measureText('TITLE', FONT_HEADER) + HEADER_EXTRA_PX;
  for (const row of input.rows) {
    const title = row.title?.trim() || 'Untitled';
    max = Math.max(max, measureText(title, FONT_CELL) + TITLE_CELL_EXTRA_PX);
  }
  return clampColumnWidth(max);
}

export function autoSizeDataColumnWidth(
  columnId: string,
  input: ColumnAutoSizeInput,
): number {
  const column = input.columns.find((c) => c.id === columnId);
  if (!column) {
    return clampColumnWidth(120);
  }
  if (column.type === 'checkbox') {
    return clampColumnWidth(CHECKBOX_COLUMN_WIDTH);
  }

  let max =
    measureText(column.name.toUpperCase(), FONT_HEADER) + HEADER_EXTRA_PX;

  for (const row of input.rows) {
    const props = input.propertiesByRowId[row.id] ?? [];
    const property = props.find((p) => p.propertyId === columnId);
    const display = formatCellDisplay(column.type, propertyPayload(property), {
      columnConfig: column.config,
    });
    max = Math.max(max, measureText(display ?? '', FONT_CELL) + CELL_EXTRA_PX);
  }

  return clampColumnWidth(max);
}

export function autoSizeAllColumnWidths(
  input: ColumnAutoSizeInput,
): Record<string, number> {
  const widths: Record<string, number> = {
    [TITLE_COLUMN_WIDTH_KEY]: autoSizeTitleColumnWidth(input),
  };
  for (const column of input.visibleCols) {
    widths[column.id] = autoSizeDataColumnWidth(column.id, input);
  }
  return widths;
}
